import { Button } from '@iris/ui/components/ui/button';
import { RefreshCw } from 'lucide-react';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useRegisterSW } from 'virtual:pwa-register/react';

const IDLE_MS = 60_000;

/**
 * Registers the service worker; when a new version waits, shows "Nouvelle version disponible — Mettre à jour"
 * (T4 §4.7). The counter also updates by itself once nobody has touched the screen for 60 s and no sheet or dialog
 * is open — never in the middle of a validation. Nothing happens in development (no worker).
 */
export function UpdatePrompt() {
  const { t } = useTranslation();
  const {
    needRefresh: [needRefresh],
    updateServiceWorker,
  } = useRegisterSW();

  useEffect(() => {
    if (!needRefresh) return undefined;
    let lastActivity = performance.now();
    const touched = () => {
      lastActivity = performance.now();
    };
    const events = ['pointerdown', 'keydown'] as const;
    for (const name of events) window.addEventListener(name, touched, { passive: true });
    const timer = window.setInterval(() => {
      const busy = document.querySelector('[role="dialog"], [role="alertdialog"]') !== null;
      if (!busy && performance.now() - lastActivity >= IDLE_MS) void updateServiceWorker(true);
    }, 5_000);
    return () => {
      for (const name of events) window.removeEventListener(name, touched);
      window.clearInterval(timer);
    };
  }, [needRefresh, updateServiceWorker]);

  if (!needRefresh) return null;
  return (
    <div
      role="status"
      className="sticky top-0 z-40 flex items-center justify-center gap-3 bg-primary px-4 py-2 text-sm text-primary-foreground print:hidden"
    >
      <span>{t('update.body')}</span>
      <Button size="sm" variant="secondary" onClick={() => void updateServiceWorker(true)}>
        <RefreshCw aria-hidden className="h-4 w-4" />
        {t('update.action')}
      </Button>
    </div>
  );
}
